import { useLayout } from "@loot-os/design-system/foundations/themeContext";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { DEFAULT_CHAT_ROOM, setChatOpen, useChatHeartbeat, useChatOpen } from "../../lib/chat.js";
import { lazyOverlay, useLazyOverlay, usePreloadWhenIdle } from "../../lib/lazyOverlay.js";
import { useShopper } from "../../lib/shopper.js";

const StoreChatDrawer = lazyOverlay(() =>
  import("./StoreChatDrawer.js").then((module) => module.StoreChatDrawer),
);

/**
 * The store's chat, mounted once beside the routes while the store has chat. The signed-in
 * shopper's presence beats whether or not chat is open. Where the theme puts chat in a drawer,
 * the drawer and its room are their own chunk, fetched once the page is idle (the header's
 * launcher is on screen) and drawn from the first open. Where it docks chat, the frame draws the
 * dock (`StoreFrame`), and this closes a phone's full-screen chat when the shopper goes to
 * another page.
 */
export function StoreChat() {
  const { user, sessionToken } = useShopper();
  useChatHeartbeat(DEFAULT_CHAT_ROOM, !!user && !!sessionToken);
  return useLayout().chat === "drawer" ? <DrawerChat /> : <CloseOnNavigate />;
}

function DrawerChat() {
  const open = useChatOpen();
  usePreloadWhenIdle(StoreChatDrawer);
  const Drawer = useLazyOverlay(StoreChatDrawer, open, () => setChatOpen(false));
  return Drawer && <Drawer open={open} />;
}

/**
 * Going to another page closes the chat where it floats over the page or fills a phone's screen,
 * as a tab bar's other tabs leave it; docked beside the page it stays as the shopper left it.
 */
function CloseOnNavigate() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const first = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on each change of page
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setChatOpen(false);
  }, [pathname]);
  return null;
}
